import { Product, Order, CustomerUser, ReviewItem, DownloadLog, LiveSiteStatistics, DocumentCategory } from '../types';
import { INITIAL_CATEGORIES } from './categoriesService';
import { INITIAL_COUNTRIES } from './countriesService';
import { filterProductsByCountry } from './slugUtils';
import { hasProductRealImage, KNOWN_DELETED_IDS } from './productImages';

export function isProductPublished(p: Product): boolean {
  if (!p || !p.id) return false;
  if (KNOWN_DELETED_IDS.includes(p.id)) return false;
  if ((p as any).isDeleted === true || (p as any).status === 'deleted') return false;
  if ((p as any).isPublished === false || (p as any).published === false) return false;
  const s = (p.status || '').toLowerCase().trim();
  if (s === 'deleted' || s === 'archived' || s === 'draft' || s === 'unpublished') return false;
  
  return s === 'published' || s === 'active' || !p.status;
}

export function computeLiveStatistics(params: {
  products?: Product[];
  orders?: Order[];
  users?: CustomerUser[];
  reviews?: ReviewItem[];
  downloads?: DownloadLog[];
  wishlistCount?: number;
}): LiveSiteStatistics {
  const {
    products = [],
    orders = [],
    users = [],
    reviews = [],
    downloads = [],
    wishlistCount = 0
  } = params;

  const publishedProds = products.filter(isProductPublished);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).getTime();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  const startOfYear = new Date(now.getFullYear(), 0, 1).getTime();

  // Active Categories & Countries count
  const activeCategoriesCount = INITIAL_CATEGORIES.length;
  const activeCountriesCount = INITIAL_COUNTRIES.length;

  // Product activity flags
  const featured = publishedProds.filter(p => p.isPopular || p.tags?.includes('featured') || (p as any).isFeatured).length;
  const trending = publishedProds.filter(p => p.isTrending || p.tags?.includes('trending')).length;
  const bestSelling = publishedProds.filter(p => (p.salesCount && p.salesCount > 0) || p.tags?.includes('bestseller') || (p as any).isBestSeller).length;
  const newProds = publishedProds.filter(p => p.isLatest || p.tags?.includes('new') || (p as any).isNew).length;
  const popularProds = publishedProds.filter(p => p.rating >= 4.5 || p.reviewCount >= 10 || p.isPopular).length;

  // Users
  const totalRegisteredUsers = users.length;
  const activeCustomers = users.filter(u => u.status === 'Active').length;
  const verifiedCustomers = users.filter(u => u.status === 'Active').length;

  // Orders
  const isOrderPaid = (o: Order): boolean => {
    const s = String(o.status || '').trim().toUpperCase();
    const ps = String(o.paymentStatus || '').trim().toUpperCase();

    // Explicit rejection/pending/failure/cancellation checks
    if (
      s.includes('CANCEL') || ps.includes('CANCEL') ||
      s.includes('REJECT') || ps.includes('REJECT') ||
      s.includes('FAIL') || ps.includes('FAIL') ||
      s.includes('REFUND') || ps.includes('REFUND') ||
      s.includes('EXPIRE') || ps.includes('EXPIRE') ||
      s.includes('PENDING') || ps.includes('PENDING') ||
      s.includes('UNPAID') || ps.includes('UNPAID')
    ) {
      return false;
    }

    // Confirmed payment states
    return (
      ps === 'PAID' ||
      ps === 'APPROVED' ||
      ps === 'CONFIRMED' ||
      s === 'COMPLETED' ||
      s === 'PAID'
    );
  };

  const isOrderCancelled = (o: Order) => {
    const s = String(o.status || '').toUpperCase();
    const ps = String(o.paymentStatus || '').toUpperCase();
    return s.includes('CANCEL') || ps.includes('CANCEL') || s.includes('REJECT') || ps.includes('REJECT') || s.includes('FAIL') || ps.includes('FAIL');
  };

  const isOrderPending = (o: Order) => {
    return !isOrderPaid(o) && !isOrderCancelled(o);
  };

  const completedOrdersList = orders.filter(isOrderPaid);
  const completedOrders = completedOrdersList.length;
  const pendingOrders = orders.filter(isOrderPending).length;
  const cancelledOrders = orders.filter(isOrderCancelled).length;

  // Revenue
  const totalSales = completedOrdersList.reduce((acc, o) => acc + (Number(o.total) || 0), 0);
  
  const revenueToday = completedOrdersList
    .filter(o => {
      const t = new Date(o.date).getTime();
      return !isNaN(t) && t >= startOfToday;
    })
    .reduce((acc, o) => acc + (Number(o.total) || 0), 0);

  const revenueThisMonth = completedOrdersList
    .filter(o => {
      const t = new Date(o.date).getTime();
      return !isNaN(t) && t >= startOfMonth;
    })
    .reduce((acc, o) => acc + (Number(o.total) || 0), 0);

  const revenueThisYear = completedOrdersList
    .filter(o => {
      const t = new Date(o.date).getTime();
      return !isNaN(t) && t >= startOfYear;
    })
    .reduce((acc, o) => acc + (Number(o.total) || 0), 0);

  // Downloads
  const totalDownloads = downloads.length;
  const downloadsToday = downloads.filter(d => new Date(d.timestamp).getTime() >= startOfToday).length;
  const downloadsThisWeek = downloads.filter(d => new Date(d.timestamp).getTime() >= startOfWeek).length;
  const downloadsThisMonth = downloads.filter(d => new Date(d.timestamp).getTime() >= startOfMonth).length;

  // Reviews
  const totalReviews = reviews.length;
  const approvedReviewsList = reviews.filter(r => r.status === 'approved');
  const approvedReviews = approvedReviewsList.length;

  let averageRating = 0;
  if (approvedReviewsList.length > 0) {
    const sum = approvedReviewsList.reduce((acc, r) => acc + r.rating, 0);
    averageRating = parseFloat((sum / approvedReviewsList.length).toFixed(2));
  } else if (publishedProds.length > 0) {
    const prodsWithRating = publishedProds.filter(p => p.rating > 0);
    if (prodsWithRating.length > 0) {
      const sum = prodsWithRating.reduce((acc, p) => acc + p.rating, 0);
      averageRating = parseFloat((sum / prodsWithRating.length).toFixed(2));
    }
  }

  // Stock
  const productsInStock = publishedProds.length;
  const productsOutOfStock = publishedProds.filter(p => (p as any).inStock === false).length;

  return {
    totalProducts: products.length,
    totalCategories: activeCategoriesCount,
    totalCountries: activeCountriesCount,
    totalPublishedProducts: publishedProds.length,
    featuredProducts: featured,
    trendingProducts: trending,
    bestSellingProducts: bestSelling,
    newProducts: newProds,
    popularProducts: popularProds,
    activeCustomers,
    totalRegisteredUsers,
    verifiedCustomers,
    completedOrders,
    pendingOrders,
    cancelledOrders,
    totalDownloads,
    downloadsToday,
    downloadsThisWeek,
    downloadsThisMonth,
    totalReviews,
    approvedReviews,
    averageRating,
    wishlistItems: wishlistCount,
    productsInStock,
    productsOutOfStock,
    totalSales,
    revenueToday,
    revenueThisMonth,
    revenueThisYear
  };
}

export function getCategoryPublishedCount(products: Product[], categoryName: DocumentCategory | string): number {
  return products.filter(p => isProductPublished(p) && p.category === categoryName).length;
}

export function getCountryPublishedCount(products: Product[], countryName: string): number {
  const published = products.filter(isProductPublished);
  return filterProductsByCountry(published, countryName).length;
}
